import type { SignoffState } from '../types';

export type SignoffTier = 'Prepared' | 'Principal' | 'Final';

export function isTierSigned(tier: SignoffTier, state: SignoffState): boolean {
  if (tier === 'Prepared')  return state === 'Prepared' || state === 'Principal' || state === 'Final';
  if (tier === 'Principal') return state === 'Principal' || state === 'Final';
  return state === 'Final';
}

// Seniority Shield: a tier is only clickable if doing so won't orphan a higher tier.
// Prepared  → clickable when null (to sign) or Prepared (to unsign, only if Prin/Final not active)
// Principal → clickable when Prepared (to sign) or Principal (to unsign, only if Final not active)
// Final     → clickable when Principal (to sign) or Final (to unsign)
export function isTierClickable(tier: SignoffTier, state: SignoffState): boolean {
  if (tier === 'Prepared')  return state === null || state === 'Prepared';
  if (tier === 'Principal') return state === 'Prepared' || state === 'Principal';
  return state === 'Principal' || state === 'Final';
}

export function computeNextSignoffState(current: SignoffState, tier: SignoffTier): SignoffState {
  if (tier === 'Prepared')  return current === null ? 'Prepared' : null;
  if (tier === 'Principal') return (current === 'Principal' || current === 'Final') ? 'Prepared' : 'Principal';
  return current === 'Final' ? 'Principal' : 'Final';
}

export function getSignoffTitle(
  tier: SignoffTier,
  isSigned: boolean,
  isClickable: boolean,
  state: SignoffState,
  cautionActive = false,
): string {
  if (!isClickable && isSigned) {
    const blocker = tier === 'Prepared'
      ? (state === 'Final' ? 'Final' : 'Principal')
      : 'Final';
    return `Must remove ${blocker} sign-off first`;
  }
  if (!isClickable && !isSigned) {
    return `Must sign ${tier === 'Principal' ? 'Prepared' : 'Principal'} first`;
  }
  if (!isSigned) return `Click to sign as ${tier}`;
  if (tier === 'Principal' && cautionActive) return 'Left-click to re-sign · Right-click to remove';
  return 'Right-click to remove sign-off';
}

/**
 * Computes whether the caution flag should display for a JE header.
 * True when the JE was saved (updatedAt) after the last Principal review (lastPrincipalReviewAt).
 */
export function hasCautionFlag(header: {
  updatedAt?: number;
  lastPrincipalReviewAt?: number | null;
}): boolean {
  return (header.updatedAt ?? 0) > (header.lastPrincipalReviewAt ?? 0);
}
